// services/analytics.ts
// COPPA-compliant analytics service for aggregated metrics
// NO individual user tracking - only aggregated data

import { collection, query, where, getDocs, doc, setDoc, Timestamp, orderBy, limit } from 'firebase/firestore';
import { db } from '../firebase/config';
import type {
    DashboardAnalytics,
    EngagementMetrics,
    ContentAnalytics,
    PlatformHealthMetrics,
    AppUsageStats,
    BarkBoardStats,
    FriendsStats,
    GamesStats,
    CreatorToolsStats,
    PollsSparksStats,
    SystemHealth,
    Alert
} from '../types';

// Import messaging analytics functions
import { getMessagingAnalytics, getMessagingQuickStats } from './adminMessaging';

// Re-export messaging analytics for convenience
export { getMessagingAnalytics, getMessagingQuickStats };

/**
 * Fetch dashboard analytics for date range
 * IMPORTANT: All metrics are aggregated - NO individual user tracking (COPPA compliant)
 */
export async function fetchDashboardAnalytics(
    startDate: Date,
    endDate: Date
): Promise<DashboardAnalytics> {
    const analytics: DashboardAnalytics = {
        totalPosts: 0,
        activePosts: 0,
        deletedPosts: 0,
        totalReports: 0,
        pendingReports: 0,
        resolvedReports: 0,
        currentQueueSize: 0,
        approvedPosts: 0,
        rejectedPosts: 0,
        moderationRate: 0,
        approvalRate: 0,
        postsByType: {},
        reportsByCategory: {},
        engagementHistory: [],
        contentHistory: [],
        platformHealth: []
    };

    const startTimestamp = Timestamp.fromDate(startDate);
    const endTimestamp = Timestamp.fromDate(endDate);

    try {
        // Fetch posts (aggregated)
        const postsQuery = query(
            collection(db, 'barkBoard', 'posts', 'items'),
            where('publishedAt', '>=', startTimestamp),
            where('publishedAt', '<=', endTimestamp)
        );
        const postsSnapshot = await getDocs(postsQuery);

        analytics.totalPosts = postsSnapshot.size;

        postsSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            const type = data.type || 'unknown';

            analytics.postsByType[type] = (analytics.postsByType[type] || 0) + 1;

            if (data.isActive === false) {
                analytics.deletedPosts++;
            } else {
                analytics.activePosts++;
            }
        });

        // Fetch reports (aggregated)
        const reportsQuery = query(
            collection(db, 'barkBoard', 'reports', 'items'),
            where('timestamp', '>=', startTimestamp),
            where('timestamp', '<=', endTimestamp)
        );
        const reportsSnapshot = await getDocs(reportsQuery);

        analytics.totalReports = reportsSnapshot.size;

        reportsSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            const category = data.category || 'Other';

            analytics.reportsByCategory[category] = (analytics.reportsByCategory[category] || 0) + 1;

            if (data.status === 'resolved') {
                analytics.resolvedReports++;
            } else if (data.status === 'pending') {
                analytics.pendingReports++;
            }
        });

        // Fetch current queue size
        const queueSnapshot = await getDocs(collection(db, 'barkBoard', 'moderation_queue', 'items'));
        analytics.currentQueueSize = queueSnapshot.size;

        // Fetch admin action logs
        const logsQuery = query(
            collection(db, 'barkBoard', 'admin_logs', 'items'),
            where('timestamp', '>=', startTimestamp),
            where('timestamp', '<=', endTimestamp)
        );
        const logsSnapshot = await getDocs(logsQuery);

        logsSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            if (data.action === 'approve_post') {
                analytics.approvedPosts++;
            } else if (data.action === 'reject_post' || data.action === 'delete_post') {
                analytics.rejectedPosts++;
            }
        });

        // Calculate rates
        analytics.moderationRate = analytics.totalReports > 0
            ? (analytics.resolvedReports / analytics.totalReports) * 100
            : 0;

        const totalModerated = analytics.approvedPosts + analytics.rejectedPosts;
        analytics.approvalRate = totalModerated > 0
            ? (analytics.approvedPosts / totalModerated) * 100
            : 0;

        // Fetch time-series data for charts
        analytics.engagementHistory = await fetchEngagementHistory(startDate, endDate);
        analytics.contentHistory = await fetchContentHistory(startDate, endDate);
        analytics.platformHealth = await fetchPlatformHealth(startDate, endDate);

    } catch (error) {
        console.error('Error fetching dashboard analytics:', error);
        throw error;
    }

    return analytics;
}

/**
 * Fetch engagement metrics time-series
 */
async function fetchEngagementHistory(
    startDate: Date,
    endDate: Date
): Promise<EngagementMetrics[]> {
    try {
        const engagementQuery = query(
            collection(db, 'barkBoard', 'analytics', 'engagement'),
            where('date', '>=', Timestamp.fromDate(startDate)),
            where('date', '<=', Timestamp.fromDate(endDate)),
            orderBy('date', 'asc')
        );
        const snapshot = await getDocs(engagementQuery);

        return snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            return {
                date: data.date.toDate(),
                totalComplimentsGiven: data.totalComplimentsGiven || 0,
                totalViewsRecorded: data.totalViewsRecorded || 0,
                activePostsCount: data.activePostsCount || 0,
                averageComplimentsPerPost: data.averageComplimentsPerPost || 0,
                averageViewsPerPost: data.averageViewsPerPost || 0,
                complimentsByType: data.complimentsByType || {},
                viewsByType: data.viewsByType || {},
                aggregatedAt: data.aggregatedAt?.toDate() || new Date()
            };
        });
    } catch (error) {
        console.error('Error fetching engagement history:', error);
        return [];
    }
}

/**
 * Fetch content analytics time-series
 */
async function fetchContentHistory(
    startDate: Date,
    endDate: Date
): Promise<ContentAnalytics[]> {
    try {
        const contentQuery = query(
            collection(db, 'barkBoard', 'analytics', 'content'),
            where('date', '>=', Timestamp.fromDate(startDate)),
            where('date', '<=', Timestamp.fromDate(endDate)),
            orderBy('date', 'asc')
        );
        const snapshot = await getDocs(contentQuery);

        return snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            return {
                date: data.date.toDate(),
                totalPostsCreated: data.totalPostsCreated || 0,
                postsByType: data.postsByType || {},
                wordCountRanges: data.wordCountRanges || { short: 0, medium: 0, long: 0 },
                topGenres: data.topGenres || [],
                topMoods: data.topMoods || [],
                aggregatedAt: data.aggregatedAt?.toDate() || new Date()
            };
        });
    } catch (error) {
        console.error('Error fetching content history:', error);
        return [];
    }
}

/**
 * Fetch platform health metrics time-series
 */
async function fetchPlatformHealth(
    startDate: Date,
    endDate: Date
): Promise<PlatformHealthMetrics[]> {
    try {
        const healthQuery = query(
            collection(db, 'barkBoard', 'analytics', 'platform_health'),
            where('date', '>=', Timestamp.fromDate(startDate)),
            where('date', '<=', Timestamp.fromDate(endDate)),
            orderBy('date', 'asc')
        );
        const snapshot = await getDocs(healthQuery);

        return snapshot.docs.map(docSnap => {
            const data = docSnap.data();
            return {
                date: data.date.toDate(),
                timestamp: data.timestamp?.toDate() || new Date(),
                totalActivePosts: data.totalActivePosts || 0,
                totalDeletedPosts: data.totalDeletedPosts || 0,
                totalFlaggedPosts: data.totalFlaggedPosts || 0,
                engagementRate: data.engagementRate || 0,
                postsPublishedLast24h: data.postsPublishedLast24h || 0,
                postsPublishedLast7d: data.postsPublishedLast7d || 0,
                postsPublishedLast30d: data.postsPublishedLast30d || 0,
                aggregatedAt: data.aggregatedAt?.toDate() || new Date()
            };
        });
    } catch (error) {
        console.error('Error fetching platform health:', error);
        return [];
    }
}

/**
 * Get quick stats for dashboard overview
 */
export async function fetchQuickStats() {
    try {
        // Get total posts count
        const postsSnapshot = await getDocs(collection(db, 'barkBoard', 'posts', 'items'));
        let totalViews = 0;
        let totalCompliments = 0;

        postsSnapshot.docs.forEach(docSnap => {
            const data = docSnap.data();
            totalViews += data.viewCount || 0;
            totalCompliments += data.complimentCount || (data.complimentCounts?.total || 0);
        });

        // Get queue size
        const queueSnapshot = await getDocs(collection(db, 'barkBoard', 'moderation_queue', 'items'));

        return {
            totalPosts: postsSnapshot.size,
            totalViews,
            totalCompliments,
            queueSize: queueSnapshot.size
        };
    } catch (error) {
        console.error('Error fetching quick stats:', error);
        return {
            totalPosts: 0,
            totalViews: 0,
            totalCompliments: 0,
            queueSize: 0
        };
    }
}

// ============================================
// APP-WIDE ANALYTICS SERVICE METHODS
// ============================================

/**
 * Get app usage stats for date range
 */
export async function getAppUsageStats(
    startDate: Date,
    endDate: Date
): Promise<AppUsageStats[]> {
    try {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const q = query(
            collection(db, 'analytics/app_usage/daily'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as AppUsageStats);
    } catch (error) {
        console.error('Error fetching app usage stats:', error);
        return [];
    }
}

/**
 * Get BarkBoard stats for date range
 */
export async function getBarkBoardStats(
    startDate: Date,
    endDate: Date
): Promise<BarkBoardStats[]> {
    try {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const q = query(
            collection(db, 'analytics/barkboard/daily'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as BarkBoardStats);
    } catch (error) {
        console.error('Error fetching BarkBoard stats:', error);
        return [];
    }
}

/**
 * Get Friends stats for date range
 */
export async function getFriendsStats(
    startDate: Date,
    endDate: Date
): Promise<FriendsStats[]> {
    try {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const q = query(
            collection(db, 'analytics/friends/daily'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as FriendsStats);
    } catch (error) {
        console.error('Error fetching friends stats:', error);
        return [];
    }
}

/**
 * Get Games stats for date range
 */
export async function getGamesStats(
    startDate: Date,
    endDate: Date
): Promise<GamesStats[]> {
    try {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const q = query(
            collection(db, 'analytics/games/daily'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as GamesStats);
    } catch (error) {
        console.error('Error fetching games stats:', error);
        return [];
    }
}

/**
 * Get Creator Tools stats for date range
 */
export async function getCreatorToolsStats(
    startDate: Date,
    endDate: Date
): Promise<CreatorToolsStats[]> {
    try {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const q = query(
            collection(db, 'analytics/creator_tools/daily'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as CreatorToolsStats);
    } catch (error) {
        console.error('Error fetching creator tools stats:', error);
        return [];
    }
}

/**
 * Get Polls & Sparks stats for date range
 */
export async function getPollsSparksStats(
    startDate: Date,
    endDate: Date
): Promise<PollsSparksStats[]> {
    try {
        const startStr = startDate.toISOString().split('T')[0];
        const endStr = endDate.toISOString().split('T')[0];

        const q = query(
            collection(db, 'analytics/polls_sparks/daily'),
            where('date', '>=', startStr),
            where('date', '<=', endStr),
            orderBy('date', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(doc => doc.data() as PollsSparksStats);
    } catch (error) {
        console.error('Error fetching polls/sparks stats:', error);
        return [];
    }
}

/**
 * Get system health status (most recent)
 */
export async function getSystemHealth(): Promise<SystemHealth | null> {
    try {
        const q = query(
            collection(db, 'monitoring/system_health/status'),
            orderBy('timestamp', 'desc'),
            limit(1)
        );

        const snapshot = await getDocs(q);
        if (snapshot.empty) return null;

        const data = snapshot.docs[0].data();
        return {
            timestamp: data.timestamp.toDate(),
            firebase: {
                status: data.firebase.status,
                lastChecked: data.firebase.lastChecked.toDate(),
                responseTime: data.firebase.responseTime,
            },
            errorRate: data.errorRate,
            crashRate: data.crashRate,
            avgResponseTime: data.avgResponseTime,
        };
    } catch (error) {
        console.error('Error fetching system health:', error);
        return null;
    }
}

/**
 * Get active alerts
 */
export async function getActiveAlerts(): Promise<Alert[]> {
    try {
        const q = query(
            collection(db, 'alerts/active/items'),
            where('resolved', '==', false),
            orderBy('timestamp', 'desc')
        );

        const snapshot = await getDocs(q);
        return snapshot.docs.map(docSnap => ({
            id: docSnap.id,
            ...docSnap.data(),
            timestamp: docSnap.data().timestamp.toDate(),
            resolvedAt: docSnap.data().resolvedAt?.toDate(),
        })) as Alert[];
    } catch (error) {
        console.error('Error fetching active alerts:', error);
        return [];
    }
}

/**
 * Resolve an alert
 */
export async function resolveAlert(alertId: string): Promise<void> {
    try {
        const alertRef = doc(db, 'alerts/active/items', alertId);
        await setDoc(alertRef, {
            resolved: true,
            resolvedAt: Timestamp.now(),
        }, { merge: true });
    } catch (error) {
        console.error('Error resolving alert:', error);
        throw error;
    }
}
